import { ShowTeamInDmsToggle } from '@/components/ShowTeamInDmsToggle';
import { useTranslation } from 'react-i18next';
import { DmPrivacySelect } from '@/components/DmPrivacySelect';
import { ReadHistoryToggle } from '@/components/ReadHistoryToggle';
import { BlockedUsersList } from '@/components/BlockedUsersList';
import { SessionsList } from '@/components/SessionsList';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { signOutHere } from '@/lib/oidcLogin';
import { disableBrowserPush } from '@/lib/webPush';
import { isPlatformEmbedded } from '@/lib/platformEmbed';

/** Privacy tab — DM-privacy, read-history sync, blocked users, sessions. */
export function PrivacyTab() {
  const { t, i18n } = useTranslation();
  // Signing out of this device ends the Cispracs ID session too. Only on the
  // web: the Desktop shell signs out through its own account menu.
  const authConfig = useQuery({ queryKey: ['authConfig'], queryFn: () => api.authConfig(), retry: false, staleTime: 60_000 });
  const desktopShell = typeof window !== 'undefined' && window.bunkerDesktop?.tabbedShell === true;
  // Inside the platform's Chat frame devices and sign-out belong to Cispracs
  // ID (the shell's /id/devices): a sign-out posted from the frame would end
  // the whole shell session on a page that cannot be framed.
  const embedded = isPlatformEmbedded();
  const signOut = authConfig.data?.oidcLogin === true && !desktopShell
    ? () => void signOutHere(disableBrowserPush, { lang: i18n.resolvedLanguage })
    : undefined;
  return (
    <div className="app-settings-groups flex flex-col gap-3">
      <div className="flex items-start justify-between gap-4 py-3">
        <div className="min-w-0 flex-1">
          <div className="app-settings-group-title text-sm text-ink">{t('prof.teamInDms.label')}</div>
          <p className="mt-1 text-xs leading-relaxed text-ink-muted">{t('prof.teamInDms.description')}</p>
        </div>
        <ShowTeamInDmsToggle />
      </div>
      <div className="flex flex-col gap-2 py-3">
        <div>
          <div className="app-settings-group-title text-base font-medium tracking-[-0.01em] text-ink">
            {t('set2.privacy.dm.title')}
          </div>
          <p className="mt-0.5 text-xs leading-relaxed text-ink-muted">
            {t('set2.privacy.dm.desc')}
          </p>
        </div>
        <DmPrivacySelect />
      </div>
      <div
        className="flex items-start justify-between gap-4 py-3"
      >
        <div className="min-w-0 flex-1">
          <div className="app-settings-group-title text-base font-medium tracking-[-0.01em] text-ink">
            {t('set2.privacy.readHistory.title')}
          </div>
          <p className="mt-0.5 text-xs leading-relaxed text-ink-muted">
            {t('set2.privacy.readHistory.desc')}
          </p>
        </div>
        <ReadHistoryToggle />
      </div>
      <div
        className="flex flex-col gap-2 py-3"
      >
        <div>
          <div className="app-settings-group-title text-base font-medium tracking-[-0.01em] text-ink">
            {t('set2.privacy.blocked.title')}
          </div>
        </div>
        <BlockedUsersList />
      </div>
      {!embedded && (
      <div
        className="flex flex-col gap-2 py-3"
      >
        <div>
          <div className="app-settings-group-title text-base font-medium tracking-[-0.01em] text-ink">
            {t('set2.privacy.sessions.title')}
          </div>
        </div>
        <SessionsList onSignOut={signOut} />
      </div>
      )}
    </div>
  );
}
